/**
 * Bookmarks hooks (TanStack Query)
 *
 * Handles bookmark creation, listing, and deletion.
 */

import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import i18n from '@/i18n';
import { bookmarksApi } from '../api/endpoints/bookmarks';
import { errorToast } from '@/hooks/errorToast';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { logInvalidateQueriesError } from '../lib/error-utils';
import type { BookmarkResponse, CreateBookmarkRequest } from '../api/types';

/**
 * Query keys for bookmarks
 */
export const bookmarkKeys = {
  all: ['bookmarks'] as const,
  lists: () => [...bookmarkKeys.all, 'list'] as const,
  list: (limit?: number, offset?: number) => [...bookmarkKeys.lists(), limit, offset] as const,
  details: () => [...bookmarkKeys.all, 'detail'] as const,
  detail: (id: string) => [...bookmarkKeys.details(), id] as const,
  byMessage: () => [...bookmarkKeys.all, 'byMessage'] as const,
};

/**
 * List user's bookmarks
 */
export function useBookmarks(limit = 50, offset = 0) {
  return useAuthenticatedQuery({
    queryKey: bookmarkKeys.list(limit, offset),
    queryFn: async () => {
      // The rows only: this hook's consumers render a list and do not page.
      // `next` (the response's `Link: rel="next"`) is there for the caller that does.
      const page = await bookmarksApi.list(limit, offset);
      return page.items;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes (align with global default)
    retry: 2, // Use default retry (align with global default)
    refetchOnMount: true, // Refetch if stale when component mounts
    refetchOnWindowFocus: false, // Don't refetch on tab switch
  });
}

/**
 * Get a single bookmark by ID
 */
export function useBookmark(id: string | null) {
  return useAuthenticatedQuery({
    queryKey: bookmarkKeys.detail(id || ''),
    queryFn: async () => {
      const result = await bookmarksApi.get(id!);
      return result;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2, // Use default retry
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
}

/**
 * Create a bookmark from a message
 */
export function useCreateBookmark() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateBookmarkRequest) => bookmarksApi.create(request),
    onSuccess: (newBookmark: BookmarkResponse) => {
      // Add new bookmark to list cache
      queryClient.setQueryData<BookmarkResponse[]>(bookmarkKeys.list(), (old) => {
        if (!old) return [newBookmark];
        return [newBookmark, ...old];
      });

      // Invalidate bookmark lists to ensure fresh data
      void queryClient
        .invalidateQueries({ queryKey: bookmarkKeys.lists() })
        .catch((err: unknown) => {
          logInvalidateQueriesError('useCreateBookmark', err);
        });
    },
    onError: () => {
      errorToast({
        context: 'createBookmark',
        description: i18n.t('bookmarks.createError'),
      });
    },
  });
}


/**
 * Manually trigger enrichment for a bookmark
 */
export function useEnrichBookmark() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (bookmarkId: string) => bookmarksApi.enrich(bookmarkId),
    onSuccess: (enrichedBookmark: BookmarkResponse) => {
      // Update detail cache with enriched data
      queryClient.setQueryData(
        bookmarkKeys.detail(enrichedBookmark.id),
        enrichedBookmark
      );

      // Invalidate list to reflect updated status
      void queryClient
        .invalidateQueries({ queryKey: bookmarkKeys.lists() })
        .catch((err: unknown) => {
          logInvalidateQueriesError('useEnrichBookmark', err);
        });
    },
    onError: () => {
      errorToast({
        context: 'enrichBookmark',
        description: i18n.t('bookmark.enrichmentFailed', { defaultValue: 'Enrichment failed' }),
      });
    },
  });
}

/**
 * Map of message_id -> bookmark_id for quick lookup.
 * Used to check if a message is already bookmarked and get its bookmark ID.
 */
export function useBookmarkedMessages() {
  const { data: bookmarks, isLoading, isError } = useBookmarks();

  // Create a map of message_id -> bookmark for O(1) lookup
  const bookmarkByMessageId = useMemo(() => {
    const map = new Map<string, BookmarkResponse>();
    if (bookmarks) {
      for (const bookmark of bookmarks) {
        map.set(bookmark.message_id, bookmark);
      }
    }
    return map;
  }, [bookmarks]);

  return {
    bookmarkByMessageId,
    isLoading,
    isError,
    getBookmarkForMessage: (messageId: string) => bookmarkByMessageId.get(messageId),
    isMessageBookmarked: (messageId: string) => bookmarkByMessageId.has(messageId),
  };
}
